import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GatewayConnection } from '../gateway-connection';
import { GatewayUrls } from '../gateway-urls';
import { AccountInfo } from './models/account-info';
import { ActiveDirectorySearchOptions } from './models/active-directory-search-options';
import { SearchType } from './models/search-type';

/**
 * Active Directory Service implementation.
 */
export class ActiveDirectory {

    public defaultDirectorySearchSizeLimit: number;
    public readonly isGatewayDomainJoined: Observable<boolean>;
    private isGatewayDomainJoinedSubject: ReplaySubject<boolean>;

    /**
     * Initializes a new instance of the ActiveDirectory class.
     * @param gateway the gateway instance.
     */
    constructor(private gateway: GatewayConnection) {
        this.defaultDirectorySearchSizeLimit = 150;
        this.isGatewayDomainJoinedSubject = new ReplaySubject(1);
        this.isGatewayDomainJoined = this.isGatewayDomainJoinedSubject.asObservable();

        this.gateway.get(GatewayUrls.adDomainRelativeUrl).subscribe({
            next: (response: any) => {
                // if response is not empty, the gateway is domain joined
                this.isGatewayDomainJoinedSubject.next(!!response);
            },
            error: () => {
                this.isGatewayDomainJoinedSubject.next(false);
                // TODO: LOG THIS ERROR
            }
        });
    }

    /**
     * Search computer list from active directory through gateway api
     */
    public search(options: ActiveDirectorySearchOptions): Observable<AccountInfo[]> {
        const type = encodeURIComponent(this.getSearchType(options));
        const searchString = encodeURIComponent(options.searchString) || '';
        if (MsftSme.isNullOrUndefined(options.sizeLimit)) {
            options.sizeLimit = this.defaultDirectorySearchSizeLimit;
        }
        const requestUrl = `${GatewayUrls.adAccountsRelativeUrl}?type=${type}&filter=${searchString}&size=${options.sizeLimit}`;
        return this.gateway.get(requestUrl).pipe(
            catchError(error => {
                const exceptionMessage = MsftSme.getValue(error, 'response.exceptionMessage');
                if (exceptionMessage) {
                    return throwError(() => exceptionMessage);
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Get the appropriate search type string from the search options
     */
    private getSearchType(options: ActiveDirectorySearchOptions): string {
        if (Array.isArray(options.searchOn)) {
            options.searchOn = options.searchOn[0];
        }

        if (MsftSme.isNullOrUndefined(options.searchOn)) {
            return 'any';
        }

        options.searchOn = options.searchOn.toLowerCase();

        if (MsftSme.isNullOrUndefined(SearchType[options.searchOn])) {
            return options.searchOn;
        }

        return SearchType[options.searchOn];
    }
}
